import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'

import { datadogRum } from '@datadog/browser-rum'

import type { RecipeUUID } from '@library/menu-service'
import { queueTrackingEvent } from '@library/tracking-integration'

type RecipeDetailSource = 'recipe_grid' | 'merch_banner'

export type MenuExperienceRecipeDetail = {
  initialFlexPortionEditorOpen?: boolean
  recipeId: RecipeUUID
  title: string
  position?: string
  source: RecipeDetailSource
  merchHeaderType?: string
}

type OpenRecipeDetailInput = MenuExperienceRecipeDetail

type MenuExperienceRecipeDetailContextValue = {
  menuId: string
  recipeDetail: MenuExperienceRecipeDetail | null
  openRecipeDetail: (recipeDetail: OpenRecipeDetailInput) => void
  closeRecipeDetail: () => void
  selectRecipe: (recipeId: RecipeUUID) => void
  updateRecipePortions: (recipeId: RecipeUUID, numPortions: number) => void
}

type MenuExperienceRecipeDetailProviderProps = {
  children: React.ReactNode
  menuId: string
  updateRecipePortions: MenuExperienceRecipeDetailContextValue['updateRecipePortions']
}

type ViewRecipeDetailsEvent = {
  action: 'view_recipe_details'
  properties: {
    menu_id: string
    recipe_id: string
    source: RecipeDetailSource
    position?: string
    merch_header_type?: string
  }
}

const MenuExperienceRecipeDetailContext = createContext<
  MenuExperienceRecipeDetailContextValue | undefined
>(undefined)

export const MenuExperienceRecipeDetailProvider = ({
  children,
  menuId,
  updateRecipePortions,
}: MenuExperienceRecipeDetailProviderProps) => {
  const [recipeDetail, setRecipeDetail] = useState<MenuExperienceRecipeDetail | null>(null)

  const openRecipeDetail = useCallback(
    (nextRecipeDetail: OpenRecipeDetailInput) => {
      setRecipeDetail(nextRecipeDetail)

      queueTrackingEvent<ViewRecipeDetailsEvent>({
        action: 'view_recipe_details',
        properties: {
          menu_id: menuId,
          recipe_id: nextRecipeDetail.recipeId,
          source: nextRecipeDetail.source,
          ...(nextRecipeDetail.position ? { position: nextRecipeDetail.position } : {}),
          ...(nextRecipeDetail.merchHeaderType
            ? { merch_header_type: nextRecipeDetail.merchHeaderType }
            : {}),
        },
      })
      datadogRum.addAction('open_menu_recipe_detail', {
        menu_id: menuId,
        recipe_id: nextRecipeDetail.recipeId,
        source: nextRecipeDetail.source,
        ...(nextRecipeDetail.merchHeaderType
          ? { merch_header_type: nextRecipeDetail.merchHeaderType }
          : {}),
      })
    },
    [menuId],
  )

  const closeRecipeDetail = useCallback(() => {
    setRecipeDetail(null)
  }, [])

  const selectRecipe = useCallback(
    (recipeId: RecipeUUID) => {
      if (recipeDetail) {
        openRecipeDetail({ ...recipeDetail, recipeId })
      }
    },
    [openRecipeDetail, recipeDetail],
  )

  const value = useMemo(
    () => ({
      menuId,
      recipeDetail,
      openRecipeDetail,
      closeRecipeDetail,
      selectRecipe,
      updateRecipePortions,
    }),
    [closeRecipeDetail, menuId, openRecipeDetail, recipeDetail, selectRecipe, updateRecipePortions],
  )

  return (
    <MenuExperienceRecipeDetailContext.Provider value={value}>
      {children}
    </MenuExperienceRecipeDetailContext.Provider>
  )
}

export const useMenuExperienceRecipeDetails = () => {
  const context = useContext(MenuExperienceRecipeDetailContext)

  if (!context) {
    throw new Error(
      'useMenuExperienceRecipeDetails must be used within MenuExperienceRecipeDetailProvider',
    )
  }

  return context
}
